import React, { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Box } from "@mui/material";

// Fix for default markers in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// Country coordinates mapping
const countryCoordinates = {
  "United States": [37.0902, -95.7129],
  "United Kingdom": [55.3781, -3.4360],
  "Canada": [56.1304, -106.3468],
  "Australia": [-25.2744, 133.7751],
  "Germany": [51.1657, 10.4515],
  "France": [46.2276, 2.2137],
  "Japan": [36.2048, 138.2529],
  "India": [20.5937, 78.9629],
  "Brazil": [-14.2350, -51.9253],
  "Mexico": [23.6345, -102.5528],
  // Add more countries as needed
};

const LeafletMap = ({ main, padd, data, location, tab }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Initialize map
    const map = L.map(mapRef.current, {
      center: [20, 0],
      zoom: 2,
      minZoom: 2,
      maxZoom: 6,
      scrollWheelZoom: false,
      attributionControl: false,
    });

    // Add dark tile layer
    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: "abcd",
      maxZoom: 19,
    }).addTo(map);

    mapInstanceRef.current = map;

    // Add markers for data points
    if (data && location) {
      addDataMarkers(map, data, location);
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Update markers when data changes
  useEffect(() => {
    if (mapInstanceRef.current && data && location) {
      // Clear existing markers
      mapInstanceRef.current.eachLayer((layer) => {
        if (layer instanceof L.Marker || layer instanceof L.CircleMarker) {
          mapInstanceRef.current.removeLayer(layer);
        }
      });

      // Add new markers
      addDataMarkers(mapInstanceRef.current, data, location);
    }
  }, [data, location, tab]);

  const addDataMarkers = (map, data, location) => {
    // Validate data
    if (!data || !Array.isArray(data) || data.length === 0) {
      console.warn('LeafletMap: No valid data to display');
      return;
    }
    
    const maxValue = Math.max(...data.map(item => item.y || item.value || 0)) || 1;
    
    data.forEach((item) => {
      const country = item.x || item.country;
      const value = item.y || item.value || 0;
      const coords = countryCoordinates[country];
      
      if (!coords) {
        console.warn(`LeafletMap: No coordinates found for country: ${country}`);
        return; // Skip this item
      }
      
      if (coords && coords.length === 2 && !isNaN(coords[0]) && !isNaN(coords[1])) {
        // Calculate radius based on value
        const radius = Math.max(5, Math.min(30, (value / maxValue) * 30));
        
        // Create circle marker
        const marker = L.circleMarker(coords, {
          radius: radius,
          fillColor: getColorForTab(tab),
          color: "#fff",
          weight: 1,
          opacity: 0.8,
          fillOpacity: 0.6,
        }).addTo(map);

        // Add popup
        marker.bindPopup(`
          <div style="text-align: center; color: #333;">
            <strong>${country}</strong><br/>
            ${tab.charAt(0).toUpperCase() + tab.slice(1)}: ${value.toLocaleString()}
          </div>
        `);

        // Hover effect
        marker.on("mouseover", function() {
          this.setStyle({
            fillOpacity: 0.9,
            weight: 2,
          });
        });

        marker.on("mouseout", function() {
          this.setStyle({
            fillOpacity: 0.6,
            weight: 1,
          });
        });
      }
    });

    // Add legend
    addLegend(map, maxValue, tab);
  };

  const getColorForTab = (tab) => {
    const colors = {
      views: "#FFD60A",
      tips: "#00D9FF",
      streams: "#4CAF50",
      follow: "#FF5722",
    };
    return colors[tab] || "#00D9FF";
  };

  const addLegend = (map, maxValue, tab) => {
    // Remove existing legend if any
    const existingLegend = document.querySelector(".leaflet-legend");
    if (existingLegend) {
      existingLegend.remove();
    }

    const legend = L.control({ position: "bottomright" });
    
    legend.onAdd = function() {
      const div = L.DomUtil.create("div", "leaflet-legend");
      const color = getColorForTab(tab);
      
      div.style.backgroundColor = "rgba(9, 9, 9, 0.9)";
      div.style.padding = "10px";
      div.style.borderRadius = "8px";
      div.style.border = "1px solid #333";
      div.style.color = "#888";
      div.style.fontSize = "12px";
      
      div.innerHTML = `
        <div style="margin-bottom: 8px; font-weight: bold;">${tab.charAt(0).toUpperCase() + tab.slice(1)}</div>
        <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
          <div style="width: 20px; height: 20px; border-radius: 50%; background: ${color}; opacity: 0.6;"></div>
          <span>${maxValue.toLocaleString()}</span>
        </div>
        <div style="display: flex; align-items: center; gap: 8px;">
          <div style="width: 10px; height: 10px; border-radius: 50%; background: ${color}; opacity: 0.6;"></div>
          <span>1</span>
        </div>
      `;
      
      return div;
    };
    
    legend.addTo(map);
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "500px",
        backgroundColor: "#090909",
        borderRadius: "8px",
        overflow: "hidden",
        position: "relative",
        "& .leaflet-container": {
          height: "100%",
          backgroundColor: "#090909",
        },
        "& .leaflet-control-zoom": {
          border: "none",
          "& a": {
            backgroundColor: "#1a1a1a",
            color: "#888",
            borderColor: "#333",
            "&:hover": {
              backgroundColor: "#2a2a2a",
              color: "#fff",
            },
          },
        },
        "& .leaflet-popup-content-wrapper": {
          backgroundColor: "#f5f5f5",
          borderRadius: "8px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        },
        "& .leaflet-popup-tip": {
          backgroundColor: "#f5f5f5",
        },
      }}
    >
      <div ref={mapRef} style={{ height: "100%", width: "100%" }} />
    </Box>
  );
};

export default LeafletMap;